import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  X, Heart, MessageCircle, Share2, Play, Pause, 
  Volume2, VolumeX, ChevronUp, ChevronDown, Film,
  User, ExternalLink, Copy, Check, SkipForward, Megaphone
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Brand domain for sharing
const BRAND_DOMAIN = 'https://ltvproduction.com';

const ReelsPlayer = ({ isOpen, onClose, initialReelId, reels: initialReels }) => {
  const navigate = useNavigate();
  const [reels, setReels] = useState(initialReels || []);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [liked, setLiked] = useState({});
  const [customer, setCustomer] = useState(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Ad-related state
  const [adSkipCountdown, setAdSkipCountdown] = useState(0);
  const [canSkipAd, setCanSkipAd] = useState(false);
  const adViewRecordedRef = useRef({});
  
  const videoRefs = useRef({});
  const containerRef = useRef(null);
  const touchStartY = useRef(0);
  const touchEndY = useRef(0);

  // Get customer data
  useEffect(() => {
    const customerData = localStorage.getItem('livestreamCustomer');
    if (customerData) {
      setCustomer(JSON.parse(customerData));
    }
  }, []);

  // Fetch initial reels if not provided
  useEffect(() => {
    if (isOpen && (!initialReels || initialReels.length === 0)) {
      fetchReels();
    }
  }, [isOpen]);

  // Set initial index based on initialReelId
  useEffect(() => {
    if (initialReelId && reels.length > 0) {
      const index = reels.findIndex(r => r.id === initialReelId);
      if (index >= 0) {
        setCurrentIndex(index);
      }
    }
  }, [initialReelId, reels]);

  // Auto-play current video when index changes
  useEffect(() => {
    if (isOpen && reels.length > 0) {
      const currentReel = reels[currentIndex];
      const videoEl = videoRefs.current[currentReel?.id];
      
      // Pause all other videos
      Object.entries(videoRefs.current).forEach(([id, video]) => {
        if (id !== currentReel?.id && video) {
          video.pause();
        }
      });
      
      // Play current video from start when switching reels
      if (videoEl) {
        videoEl.currentTime = 0;
        videoEl.muted = isMuted;
        videoEl.play().catch(() => {});
        setIsPlaying(true);
      }
      
      // Handle ad-specific logic
      if (currentReel?.is_ad) {
        // Reset skip state for ads
        setCanSkipAd(false);
        setAdSkipCountdown(currentReel.skip_after_seconds || 5);
        
        // Record ad view (only once per ad)
        if (!adViewRecordedRef.current[currentReel.id]) {
          recordAdView(currentReel.id);
          adViewRecordedRef.current[currentReel.id] = true;
        }
      } else {
        // Regular reel - record view
        if (currentReel?.id) {
          recordView(currentReel.id);
        }
      }
    }
  }, [currentIndex, isOpen, reels]);
  
  // Ad skip countdown timer
  useEffect(() => {
    const currentReel = reels[currentIndex];
    if (!currentReel?.is_ad || !isPlaying || adSkipCountdown <= 0) {
      if (adSkipCountdown <= 0 && currentReel?.is_ad) {
        setCanSkipAd(true);
      }
      return;
    }
    
    const timer = setInterval(() => {
      setAdSkipCountdown(prev => {
        if (prev <= 1) {
          setCanSkipAd(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [currentIndex, isPlaying, adSkipCountdown, reels]);

  // Sync video playback with isPlaying state
  useEffect(() => {
    if (isOpen && reels.length > 0) {
      const currentReel = reels[currentIndex];
      const videoEl = videoRefs.current[currentReel?.id];
      
      if (videoEl) {
        if (isPlaying) {
          videoEl.play().catch(() => {});
        } else {
          videoEl.pause();
        }
      }
    }
  }, [isPlaying]);

  // Sync mute state
  useEffect(() => {
    if (isOpen && reels.length > 0) {
      const currentReel = reels[currentIndex];
      const videoEl = videoRefs.current[currentReel?.id];
      if (videoEl) {
        videoEl.muted = isMuted;
      }
    }
  }, [isMuted]);

  // Fetch more reels
  const fetchReels = async (startId = null) => {
    if (loading) return;
    setLoading(true);
    
    try {
      const url = startId 
        ? `${API}/api/reels/feed?start_id=${startId}&limit=10`
        : `${API}/api/reels/feed?limit=10`;
      
      const response = await axios.get(url);
      const newReels = response.data.reels || [];
      
      if (startId) {
        setReels(prev => [...prev, ...newReels]);
      } else {
        setReels(newReels);
      }
      
      setHasMore(response.data.has_more);
    } catch (err) {
      console.error('Error fetching reels:', err);
    } finally {
      setLoading(false);
    }
  };

  // Record view
  const recordView = async (reelId) => {
    try {
      await axios.post(`${API}/api/reels/${reelId}/view`);
    } catch (err) {
      console.error('Error recording view:', err);
    }
  };
  
  // Record ad view/impression
  const recordAdView = async (adId) => {
    try {
      await axios.post(`${API}/api/ad-reels/${adId}/view`);
    } catch (err) {
      console.error('Error recording ad view:', err);
    }
  };
  
  // Record ad completion
  const recordAdCompletion = async (adId) => {
    try {
      await axios.post(`${API}/api/ad-reels/${adId}/complete`);
    } catch (err) {
      console.error('Error recording ad completion:', err);
    }
  };
  
  // Record ad click
  const recordAdClick = async (adId) => {
    try {
      await axios.post(`${API}/api/ad-reels/${adId}/click`);
    } catch (err) {
      console.error('Error recording ad click:', err);
    }
  };
  
  // Record ad skip
  const recordAdSkip = async (adId) => {
    try {
      await axios.post(`${API}/api/ad-reels/${adId}/skip`);
    } catch (err) {
      console.error('Error recording ad skip:', err);
    }
  };
  
  // Skip current ad
  const skipAd = () => {
    const currentReel = reels[currentIndex];
    if (currentReel?.is_ad && canSkipAd) {
      recordAdSkip(currentReel.id);
      goToNext();
    }
  };
  
  // Handle CTA click
  const handleCtaClick = (reel) => {
    if (reel.cta_url) {
      recordAdClick(reel.id);
      window.open(reel.cta_url, '_blank');
    }
  };

  // Handle like
  const handleLike = async (reelId) => {
    if (!customer) {
      navigate('/live/login?from=vod&redirect=/vod');
      onClose();
      return;
    }
    
    try {
      const response = await axios.post(`${API}/api/reels/${reelId}/like`, {
        user_id: customer.id
      });
      
      setLiked(prev => ({ ...prev, [reelId]: response.data.liked }));
      
      // Update like count in reels array
      setReels(prev => prev.map(r => {
        if (r.id === reelId) {
          return {
            ...r,
            like_count: response.data.liked ? r.like_count + 1 : r.like_count - 1
          };
        }
        return r;
      }));
    } catch (err) {
      console.error('Error liking reel:', err);
    }
  };

  // Navigate to next reel (with loop)
  const goToNext = useCallback(() => {
    if (currentIndex < reels.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      // Loop back to the first reel
      setCurrentIndex(0);
    }
  }, [currentIndex, reels.length]);

  // Navigate to previous reel (with loop)
  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    } else {
      // Loop to the last reel
      setCurrentIndex(reels.length - 1);
    }
  }, [currentIndex, reels.length]);

  // Handle swipe gestures
  const handleTouchStart = (e) => {
    // Don't track swipes that start on buttons or interactive elements
    if (e.target.closest('button') || e.target.closest('a') || e.target.closest('[data-no-swipe]')) {
      touchStartY.current = null;
      return;
    }
    touchStartY.current = e.touches[0].clientY;
    touchEndY.current = e.touches[0].clientY; // Initialize to same position
  };

  const handleTouchMove = (e) => {
    if (touchStartY.current === null) return;
    touchEndY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    if (touchStartY.current === null) return;
    
    const diff = touchStartY.current - touchEndY.current;
    const threshold = 50; // Minimum swipe distance in pixels
    
    // If movement is less than threshold, it's a tap - toggle play/pause
    if (Math.abs(diff) < threshold) {
      // This is a tap, not a swipe - toggle play/pause
      const videoEl = videoRefs.current[reels[currentIndex]?.id];
      if (videoEl) {
        if (videoEl.paused) {
          videoEl.play().catch(() => {});
          setIsPlaying(true);
        } else {
          videoEl.pause();
          setIsPlaying(false);
        }
      }
    } else if (diff > threshold) {
      // Swiped up - go to next
      goToNext();
    } else if (diff < -threshold) {
      // Swiped down - go to previous
      goToPrevious();
    }
    
    // Reset touch tracking
    touchStartY.current = null;
    touchEndY.current = 0;
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'ArrowUp':
          goToPrevious();
          break;
        case 'ArrowDown':
          goToNext();
          break;
        case ' ':
          e.preventDefault();
          setIsPlaying(prev => !prev);
          break;
        case 'm':
          setIsMuted(prev => !prev);
          break;
        case 'Escape':
          onClose();
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, goToNext, goToPrevious, onClose]);

  // Handle video end
  const handleVideoEnd = () => {
    // If this was an ad, record completion
    const currentReel = reels[currentIndex];
    if (currentReel?.is_ad) {
      recordAdCompletion(currentReel.id);
    }
    goToNext();
  };

  // Navigate to full video
  const goToFullVideo = (reel) => {
    onClose();
    if (reel.linked_video_slug) {
      navigate(`/vod/video/${reel.linked_video_slug}?autoplay=true`);
    } else if (reel.linked_video_id) {
      navigate(`/vod/video/${reel.linked_video_id}?autoplay=true`);
    }
  };

  // Navigate to creator
  const goToCreator = (reel) => {
    onClose();
    if (reel.creator_slug) {
      navigate(`/c/${reel.creator_slug}`);
    }
  };

  // Get share URL for a reel (using API endpoint that serves OG tags to crawlers)
  const getShareUrl = (reel) => {
    // Use the smart share endpoint that serves OG tags to social media crawlers
    // and redirects normal users to the actual reel page
    return `${BRAND_DOMAIN}/api/share/reel/${reel.id}`;
  };

  // Handle share
  const handleShare = async (reel) => {
    const shareUrl = getShareUrl(reel);
    const shareData = {
      title: reel.title,
      text: `Check out "${reel.title}" on LTV VOD`,
      url: shareUrl
    };

    // Try native share API first (mobile)
    if (navigator.share && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    }
    
    // Show share menu for desktop
    setShowShareMenu(true);
  };

  // Copy link to clipboard
  const copyLink = async (reel) => {
    const shareUrl = getShareUrl(reel);
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        setShowShareMenu(false);
      }, 2000);
    } catch (err) {
      console.error('Copy failed:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        setShowShareMenu(false);
      }, 2000);
    }
  };

  // Share to specific platforms
  const shareToTwitter = (reel) => {
    const shareUrl = getShareUrl(reel);
    const text = encodeURIComponent(`Check out "${reel.title}" on LTV VOD`);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(shareUrl)}`, '_blank');
    setShowShareMenu(false);
  };

  const shareToFacebook = (reel) => {
    const shareUrl = getShareUrl(reel);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
    setShowShareMenu(false);
  };

  const shareToWhatsApp = (reel) => {
    const shareUrl = getShareUrl(reel);
    const text = encodeURIComponent(`Check out "${reel.title}" on LTV VOD: ${shareUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
    setShowShareMenu(false);
  };

  if (!isOpen) return null;

  const currentReel = reels[currentIndex];

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black touch-pan-y"
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ touchAction: 'pan-y' }}
    >
      {/* Close Button */}
      <button 
        onClick={onClose}
        className="absolute top-4 right-4 z-50 p-2 bg-black/50 rounded-full hover:bg-black/70 transition"
        data-testid="reels-close-btn"
      >
        <X className="h-6 w-6 text-white" />
      </button>

      {/* Navigation Arrows (Desktop) */}
      <div className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-50 flex-col gap-2">
        <button 
          onClick={goToPrevious}
          disabled={currentIndex === 0}
          className={`p-3 bg-white/10 rounded-full hover:bg-white/20 transition ${currentIndex === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
        >
          <ChevronUp className="h-6 w-6 text-white" />
        </button>
        <button 
          onClick={goToNext}
          disabled={currentIndex === reels.length - 1 && !hasMore}
          className={`p-3 bg-white/10 rounded-full hover:bg-white/20 transition ${currentIndex === reels.length - 1 && !hasMore ? 'opacity-30 cursor-not-allowed' : ''}`}
        >
          <ChevronDown className="h-6 w-6 text-white" />
        </button>
      </div>

      {/* Reels Container */}
      <div className="h-full w-full flex items-center justify-center">
        {reels.length === 0 ? (
          <div className="text-white text-center">
            <Film className="h-16 w-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg">No reels available</p>
          </div>
        ) : currentReel ? (
          <div className="relative h-full w-full max-w-[500px] mx-auto flex items-center">
            {/* Video Player */}
            <div 
              className="relative w-full aspect-[9/16] max-h-full bg-black rounded-lg overflow-hidden"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <video
                ref={el => videoRefs.current[currentReel.id] = el}
                src={currentReel.video_url}
                poster={currentReel.thumbnail_url}
                className="w-full h-full object-cover"
                loop={false}
                playsInline
                muted={isMuted}
                controls={false}
                controlsList="nodownload noplaybackrate"
                onContextMenu={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.stopPropagation();
                  const videoEl = videoRefs.current[currentReel.id];
                  if (videoEl) {
                    if (videoEl.paused) {
                      videoEl.play().catch(() => {});
                      setIsPlaying(true);
                    } else {
                      videoEl.pause();
                      setIsPlaying(false);
                    }
                  }
                }}
                onEnded={handleVideoEnd}
                data-testid="reel-video-player"
              />
              
              {/* Play/Pause Overlay */}
              {!isPlaying && (
                <div 
                  className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer z-20"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsPlaying(true);
                    const videoEl = videoRefs.current[currentReel.id];
                    if (videoEl) {
                      videoEl.play().catch(() => {});
                    }
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                  }}
                  onTouchMove={(e) => {
                    e.stopPropagation();
                  }}
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    // Play video on touch end
                    setIsPlaying(true);
                    const videoEl = videoRefs.current[currentReel.id];
                    if (videoEl) {
                      videoEl.play().catch(() => {});
                    }
                  }}
                  data-testid="reel-play-overlay"
                  data-no-swipe="true"
                >
                  <Play className="h-20 w-20 text-white opacity-80 fill-white" />
                </div>
              )}
              
              {/* Gradient Overlay for Text */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
              
              {/* Sponsored/Ad Badge */}
              {currentReel.is_ad && (
                <div className="absolute top-4 left-4 z-30" data-no-swipe="true">
                  <div className="flex items-center gap-2 bg-black/60 px-3 py-1.5 rounded-full">
                    <Megaphone className="h-3.5 w-3.5 text-yellow-400" />
                    <span className="text-white text-xs font-medium">Sponsored</span>
                  </div>
                </div>
              )}
              
              {/* Skip Ad Button */}
              {currentReel.is_ad && (
                <div className="absolute top-4 right-4 z-30" data-no-swipe="true">
                  {canSkipAd ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); skipAd(); }}
                      onTouchEnd={(e) => { e.stopPropagation(); skipAd(); }}
                      className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-full transition"
                    >
                      <span className="text-white text-xs font-medium">Skip Ad</span>
                      <SkipForward className="h-3.5 w-3.5 text-white" />
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 bg-black/60 px-3 py-1.5 rounded-full">
                      <span className="text-white text-xs font-medium">Skip in {adSkipCountdown}s</span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Bottom Info */}
              <div className="absolute bottom-20 left-0 right-16 p-4" data-no-swipe="true">
                {/* Ad-specific content */}
                {currentReel.is_ad ? (
                  <>
                    {/* Advertiser Info */}
                    <div className="flex items-center gap-3 mb-2">
                      {currentReel.advertiser_logo_url ? (
                        <img 
                          src={currentReel.advertiser_logo_url} 
                          alt={currentReel.advertiser_name}
                          className="w-9 h-9 rounded-full object-cover border-2 border-yellow-400"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-yellow-500 flex items-center justify-center border-2 border-yellow-400">
                          <Megaphone className="h-4 w-4 text-white" />
                        </div>
                      )}
                      <span className="font-semibold text-white text-sm">
                        {currentReel.advertiser_name || 'Sponsored'}
                      </span>
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-white font-medium text-sm mb-1 line-clamp-2">
                      {currentReel.title}
                    </h3>
                    
                    {/* Description */}
                    {currentReel.description && (
                      <p className="text-gray-300 text-xs line-clamp-1 mb-2">
                        {currentReel.description}
                      </p>
                    )}
                    
                    {/* CTA Button */}
                    {currentReel.cta_text && currentReel.cta_url && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleCtaClick(currentReel); }}
                        onTouchEnd={(e) => { e.stopPropagation(); }}
                        className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-black rounded-full text-xs font-bold hover:bg-yellow-400 transition"
                        data-testid="ad-cta-btn"
                      >
                        <ExternalLink className="h-3 w-3" />
                        {currentReel.cta_text}
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    {/* Regular reel - Creator Info */}
                    <div 
                      className="flex items-center gap-3 mb-2 cursor-pointer"
                      onClick={(e) => { e.stopPropagation(); goToCreator(currentReel); }}
                      onTouchEnd={(e) => { e.stopPropagation(); }}
                    >
                      {currentReel.creator_profile_image ? (
                        <img 
                          src={currentReel.creator_profile_image} 
                          alt={currentReel.creator_name}
                          className="w-9 h-9 rounded-full object-cover border-2 border-white"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-purple-600 flex items-center justify-center border-2 border-white">
                          <User className="h-4 w-4 text-white" />
                        </div>
                      )}
                      <span className="font-semibold text-white text-sm">
                        {currentReel.creator_name || 'Creator'}
                      </span>
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-white font-medium text-sm mb-1 line-clamp-2">
                      {currentReel.title}
                    </h3>
                    
                    {/* Description - smaller on mobile */}
                    {currentReel.description && (
                      <p className="text-gray-300 text-xs line-clamp-1 mb-2">
                        {currentReel.description}
                      </p>
                    )}
                    
                    {/* Watch Full Video Button */}
                    {currentReel.linked_video_id && (
                      <button
                        onClick={(e) => { e.stopPropagation(); goToFullVideo(currentReel); }}
                        onTouchEnd={(e) => { e.stopPropagation(); }}
                        className="flex items-center gap-2 px-3 py-1.5 bg-white text-black rounded-full text-xs font-medium hover:bg-gray-200 transition"
                        data-testid="watch-full-video-btn"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Watch Full Video
                      </button>
                    )}
                  </>
                )}
              </div>
              
              {/* Right Side Actions - Hide for ads except mute */}
              <div className="absolute right-2 bottom-24 flex flex-col items-center gap-4" data-no-swipe="true">
                {!currentReel.is_ad && (
                  <>
                    {/* Like */}
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleLike(currentReel.id); }}
                      onTouchEnd={(e) => { e.stopPropagation(); }}
                      className="flex flex-col items-center"
                      data-testid="reel-like-btn"
                    >
                      <div className={`p-2.5 rounded-full ${liked[currentReel.id] ? 'bg-red-500' : 'bg-white/20'} hover:bg-white/30 transition`}>
                        <Heart className={`h-5 w-5 ${liked[currentReel.id] ? 'text-white fill-white' : 'text-white'}`} />
                      </div>
                      <span className="text-white text-[10px] mt-1">{currentReel.like_count || 0}</span>
                    </button>
                    
                    {/* Comment */}
                    <button 
                      onClick={(e) => e.stopPropagation()}
                      onTouchEnd={(e) => { e.stopPropagation(); }}
                      className="flex flex-col items-center"
                    >
                      <div className="p-2.5 rounded-full bg-white/20 hover:bg-white/30 transition">
                        <MessageCircle className="h-5 w-5 text-white" />
                      </div>
                      <span className="text-white text-[10px] mt-1">{currentReel.comment_count || 0}</span>
                    </button>
                    
                    {/* Share */}
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleShare(currentReel); }}
                      onTouchEnd={(e) => { e.stopPropagation(); }}
                      className="flex flex-col items-center"
                      data-testid="reel-share-btn"
                    >
                      <div className="p-2.5 rounded-full bg-white/20 hover:bg-white/30 transition">
                        <Share2 className="h-5 w-5 text-white" />
                      </div>
                      <span className="text-white text-[10px] mt-1">Share</span>
                    </button>
                  </>
                )}
                
                {/* Mute/Unmute - always visible */}
                <button 
                  onClick={(e) => { e.stopPropagation(); setIsMuted(prev => !prev); }}
                  onTouchEnd={(e) => { e.stopPropagation(); }}
                  className="p-2.5 rounded-full bg-white/20 hover:bg-white/30 transition"
                >
                  {isMuted ? (
                    <VolumeX className="h-5 w-5 text-white" />
                  ) : (
                    <Volume2 className="h-5 w-5 text-white" />
                  )}
                </button>
              </div>

              {/* Share Menu Popup */}
              {showShareMenu && (
                <div 
                  className="absolute inset-0 bg-black/60 flex items-end justify-center z-50"
                  onClick={() => setShowShareMenu(false)}
                >
                  <div 
                    className="bg-gray-900 rounded-t-2xl w-full max-w-md p-6 pb-8"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-white text-lg font-semibold">Share</h3>
                      <button 
                        onClick={() => setShowShareMenu(false)}
                        className="p-1 hover:bg-white/10 rounded-full"
                      >
                        <X className="h-5 w-5 text-gray-400" />
                      </button>
                    </div>
                    
                    {/* Share Options */}
                    <div className="grid grid-cols-4 gap-4 mb-6">
                      {/* Copy Link */}
                      <button 
                        onClick={() => copyLink(currentReel)}
                        className="flex flex-col items-center gap-2"
                      >
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center ${copied ? 'bg-green-600' : 'bg-gray-700'}`}>
                          {copied ? <Check className="h-6 w-6 text-white" /> : <Copy className="h-6 w-6 text-white" />}
                        </div>
                        <span className="text-white text-xs">{copied ? 'Copied!' : 'Copy link'}</span>
                      </button>
                      
                      {/* Twitter/X */}
                      <button 
                        onClick={() => shareToTwitter(currentReel)}
                        className="flex flex-col items-center gap-2"
                      >
                        <div className="w-14 h-14 rounded-full bg-black flex items-center justify-center border border-gray-600">
                          <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                          </svg>
                        </div>
                        <span className="text-white text-xs">X</span>
                      </button>
                      
                      {/* Facebook */}
                      <button 
                        onClick={() => shareToFacebook(currentReel)}
                        className="flex flex-col items-center gap-2"
                      >
                        <div className="w-14 h-14 rounded-full bg-[#1877F2] flex items-center justify-center">
                          <svg className="h-6 w-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                        </div>
                        <span className="text-white text-xs">Facebook</span>
                      </button>
                      
                      {/* WhatsApp */}
                      <button 
                        onClick={() => shareToWhatsApp(currentReel)}
                        className="flex flex-col items-center gap-2"
                      >
                        <div className="w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center">
                          <svg className="h-6 w-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                        </div>
                        <span className="text-white text-xs">WhatsApp</span>
                      </button>
                    </div>
                    
                    {/* Link Preview */}
                    <div className="bg-gray-800 rounded-lg p-3 flex items-center gap-3">
                      <div className="w-12 h-12 rounded bg-gray-700 flex-shrink-0 overflow-hidden">
                        {currentReel.thumbnail_url ? (
                          <img src={currentReel.thumbnail_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Film className="w-full h-full p-2 text-gray-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{currentReel.title}</p>
                        <p className="text-gray-400 text-xs truncate">ltvproduction.com</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Progress Indicator */}
              <div className="absolute top-4 left-4 right-4 flex gap-1">
                {reels.slice(0, Math.min(reels.length, 10)).map((_, idx) => (
                  <div 
                    key={idx}
                    className={`h-1 flex-1 rounded-full ${idx === currentIndex ? 'bg-white' : 'bg-white/30'}`}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
      
      {/* Loading Indicator */}
      {loading && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-white" />
        </div>
      )}
      
      {/* Swipe Hint (Mobile) - positioned above the safe area */}
      <div className="md:hidden absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-xs">
        Swipe up for next
      </div>
    </div>
  );
};

export default ReelsPlayer;
